import React, { useState, useRef, useLayoutEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTopics } from "../../context/TopicContext";
import { useToast } from "../../context/ToastContext";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaEdit,
  FaTrash,
  FaArrowUp,
  FaPlus,
  FaUser,
  FaSignOutAlt,
  FaPen,
  FaCompress,
} from "react-icons/fa";
import Search from "../search/Search";
import { useMode } from "../../context/ModeContext";
import theme from "../../styles/theme";
import {
  Button,
  IconButton,
  Input,
  Form,
  ButtonGroup,
} from "../common/controls";

const Nav = styled.nav`
  background-color: ${theme.color.bg};
  border-bottom: 1px solid ${theme.color.borderSubtle};
  padding: ${theme.space.sm} ${theme.space.md};
  position: sticky;
  top: 0;
  z-index: ${theme.z.nav};
`;

// Single wrapping row. Nothing here may have a fixed width that outgrows the
// viewport — the window is routinely a few hundred pixels wide.
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.sm};
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
`;

const Brand = styled(Link)`
  color: ${theme.color.text};
  text-decoration: none;
  font-size: ${theme.font.lg};
  font-weight: 700;
  line-height: 1;
  flex: 0 0 auto;

  &:focus-visible {
    outline: 2px solid ${theme.color.focus};
    outline-offset: 2px;
  }
`;

// Takes the slack so the icon buttons keep their size and stay reachable.
const SearchContainer = styled.div`
  position: relative;
  flex: 1 1 120px;
  min-width: 90px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs};
  flex: 0 0 auto;
`;

// Separates the topic actions from the account actions without a hard divider
// eating width.
const Spacer = styled.span`
  width: ${theme.space.sm};
  flex: 0 0 auto;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${theme.z.nav - 1};
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.space.md};
  z-index: ${theme.z.modal};
`;

const ModalContent = styled.div`
  background: ${theme.color.surface};
  padding: ${theme.space.lg};
  border-radius: ${theme.radius.md};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;

  h3 {
    font-size: ${theme.font.lg};
    margin-bottom: ${theme.space.md};
    color: ${theme.color.text};
  }
`;

const EditorContainer = styled.div`
  .ql-editor {
    min-height: 180px;
    max-height: 45vh;
    overflow-y: auto;
  }

  .ql-container {
    height: auto;
    min-height: 180px;
    font-size: ${theme.font.md};
  }
`;

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { createTopic, deleteTopic, addSubtopic } = useTopics();
  const { showToast } = useToast();
  const { isEditMode, toggleEditMode } = useMode();
  const navRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTopicName, setNewTopicName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [newSubtopic, setNewSubtopic] = useState({ content: "" });
  const [showSearch, setShowSearch] = useState(false);
  const [saving, setSaving] = useState(false);

  const isTopicDetail = location.pathname.includes("/topic/");
  const topicId = isTopicDetail ? location.pathname.split("/")[2] : null;

  // Only a topic has a mode; the topic list is always editable.
  const editable = !isTopicDetail || isEditMode(topicId);

  // Publish the navbar's real height as --nav-h. It varies — one row in live
  // mode, two when the edit controls wrap — and the reading card sizes itself
  // against it, so a guessed constant wastes space in a small square window.
  //
  // Re-measured on the things that actually change the row count: the mode
  // toggle, entering/leaving a topic, signing in or out, and window resize.
  useLayoutEffect(() => {
    const publish = () => {
      // Logged out the navbar is not rendered; report zero height so the auth
      // screens centre in the full viewport rather than reserving a phantom bar.
      const el = navRef.current;
      const height = user && el ? el.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty("--nav-h", `${height}px`);
    };

    publish();
    window.addEventListener("resize", publish);
    return () => window.removeEventListener("resize", publish);
  }, [editable, isTopicDetail, user]);

  const closeModal = () => {
    setNewTopicName("");
    setNewSubtopic({ content: "" });
    setIsModalOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Shrink to a teleprompter-sized window, parked at the top-centre of the
  // screen so it sits just under the webcam.
  //
  // Browsers refuse resizeTo() on a window the script did not open, so from a
  // normal tab the only way to get a small window is to open a new one. Inside
  // that popup (window.opener is set) resizeTo is permitted, so the same button
  // re-snaps it to size.
  const COMPACT_SIZE = 430; // square: height matches width

  const shrinkToCompact = () => {
    const left = Math.max(0, Math.round((window.screen.availWidth - COMPACT_SIZE) / 2));
    const top = 0;

    if (window.opener) {
      window.resizeTo(COMPACT_SIZE, COMPACT_SIZE);
      window.moveTo(left, top);
      return;
    }

    const popup = window.open(
      location.pathname,
      "interview-teleprompter",
      `width=${COMPACT_SIZE},height=${COMPACT_SIZE},left=${left},top=${top}`
    );

    if (!popup) {
      showToast("Allow pop-ups for this site to open the compact window", "error");
      return;
    }
    popup.focus();
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!newTopicName.trim()) {
      showToast("Please enter a topic name", "error");
      return;
    }

    try {
      setSaving(true);
      await createTopic(newTopicName.trim());
      closeModal();
      showToast("Topic created successfully", "success");
    } catch (error) {
      console.error("Topic creation error:", error);
      showToast(
        error.response?.data?.message ||
          error.message ||
          "Failed to create topic",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubtopic = async (e) => {
    e.preventDefault();
    if (!newTopicName.trim()) {
      showToast("Please enter a subtopic name", "error");
      return;
    }

    try {
      setSaving(true);
      await addSubtopic(topicId, {
        name: newTopicName.trim(),
        content: newSubtopic.content,
      });
      closeModal();
      showToast("Subtopic created successfully", "success");
    } catch (error) {
      console.error("Subtopic creation error:", error);
      showToast(
        error.response?.data?.message ||
          error.message ||
          "Failed to create subtopic",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this topic? All subtopics will be deleted."
      )
    ) {
      return;
    }

    try {
      await deleteTopic(topicId);
      showToast("Topic deleted successfully", "success");
      navigate("/");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to delete topic",
        "error"
      );
    }
  };

  // No navbar on the auth screens: login and register are self-branded and
  // link to each other, so a bar with a redundant Login/Register would only
  // duplicate them and steal reading space.
  if (!user) {
    return null;
  }

  return (
    <Nav ref={navRef}>
      <Row>
        <Brand to="/">Topics</Brand>

        {user && (
          <>
            <SearchContainer>
              <Input
                type="search"
                placeholder="Search..."
                aria-label="Search topics and subtopics"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearch(true)}
              />
              {showSearch && (
                <>
                  <Backdrop onClick={() => setShowSearch(false)} />
                  <Search
                    searchQuery={searchQuery}
                    onClose={() => {
                      setShowSearch(false);
                      setSearchQuery("");
                    }}
                  />
                </>
              )}
            </SearchContainer>

            <Actions>
              {/* Up, not left: navigation here is up a hierarchy
                  (subtopic → topic → all topics), matching the card. */}
              {isTopicDetail && (
                <IconButton
                  onClick={() => navigate("/")}
                  aria-label="Back to all topics"
                  title="Back to all topics"
                >
                  <FaArrowUp />
                </IconButton>
              )}

              {/* Mode switch for this topic. Always visible, or live mode
                  would be a one-way door. */}
              {isTopicDetail && (
                <IconButton
                  $active={editable}
                  aria-pressed={editable}
                  onClick={() => toggleEditMode(topicId)}
                  aria-label={
                    editable ? "Switch to live mode" : "Switch to edit mode"
                  }
                  title={
                    editable
                      ? "Edit mode — click for live mode"
                      : "Live mode — click to edit"
                  }
                >
                  <FaPen />
                </IconButton>
              )}

              {/* Live mode only: this is the interview-time control. */}
              {isTopicDetail && !editable && (
                <IconButton
                  onClick={shrinkToCompact}
                  aria-label="Shrink to compact window"
                  title="Shrink to compact window"
                >
                  <FaCompress />
                </IconButton>
              )}

              {editable && (
                <IconButton
                  onClick={() => setIsModalOpen(true)}
                  aria-label={isTopicDetail ? "Add subtopic" : "Add topic"}
                  title={isTopicDetail ? "Add subtopic" : "Add topic"}
                >
                  <FaPlus />
                </IconButton>
              )}
              {isTopicDetail && editable && (
                <>
                  <IconButton
                    onClick={() => navigate(`/topic/${topicId}/edit`)}
                    aria-label="Rename topic"
                    title="Rename topic"
                  >
                    <FaEdit />
                  </IconButton>
                  <IconButton
                    $danger
                    onClick={handleDeleteTopic}
                    aria-label="Delete topic"
                    title="Delete topic"
                  >
                    <FaTrash />
                  </IconButton>
                </>
              )}
            </Actions>
          </>
        )}

        <Spacer />

        <Actions>
          <IconButton
            as={Link}
            to="/profile"
            aria-label="Profile"
            title="Profile"
          >
            <FaUser />
          </IconButton>
          <IconButton
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
          >
            <FaSignOutAlt />
          </IconButton>
        </Actions>
      </Row>

      {isModalOpen && (
        <Modal
          role="dialog"
          aria-modal="true"
          aria-label={isTopicDetail ? "Create new subtopic" : "Create new topic"}
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <ModalContent>
            <h3>{isTopicDetail ? "Create New Subtopic" : "Create New Topic"}</h3>
            <Form
              onSubmit={isTopicDetail ? handleCreateSubtopic : handleCreateTopic}
            >
              <Input
                type="text"
                autoFocus
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                placeholder={isTopicDetail ? "Subtopic name" : "Topic name"}
                aria-label={isTopicDetail ? "Subtopic name" : "Topic name"}
                required
              />
              {isTopicDetail && (
                <EditorContainer>
                  <ReactQuill
                    value={newSubtopic.content}
                    onChange={(content) => setNewSubtopic({ content })}
                    modules={quillModules}
                    placeholder="Enter subtopic content..."
                  />
                </EditorContainer>
              )}
              <ButtonGroup>
                <Button type="button" $secondary onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? "Creating..." : "Create"}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Nav>
  );
};

export default Navbar;
